import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";
import { Loader2, Link2, Unlink, AlertTriangle, CheckCircle2, RefreshCw, ShieldAlert } from "lucide-react";

const eur = (n) => `€ ${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleString("it-IT") : "—");

const TABS = [
  { key: "pending", label: "Da verificare", icon: RefreshCw },
  { key: "matched", label: "Riconciliate", icon: CheckCircle2 },
  { key: "conflict", label: "Conflitti", icon: AlertTriangle },
];

const STATUS_BADGE = {
  pending: { label: "Da verificare", cls: "bg-amber-100 text-amber-700" },
  matched: { label: "Riconciliata", cls: "bg-emerald-100 text-emerald-700" },
  conflict: { label: "Conflitto", cls: "bg-red-100 text-red-700" },
  none: { label: "Non riconciliata", cls: "bg-secondary text-muted-foreground" },
};

function Field({ label, value, testid }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-sm font-medium truncate" data-testid={testid}>{value ?? "—"}</p>
    </div>
  );
}

export default function ReconciliationPanel() {
  const { user } = useAuth();
  const canReconcile = user?.role === "admin" || user?.role === "manager";
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // { sale, action: 'match'|'reject' }

  const load = useCallback(async (st) => {
    setLoading(true);
    try {
      const { data } = await api.get("/reconciliation", { params: { status: st } });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(status); }, [status, load]);

  const doAction = async () => {
    if (!confirm) return;
    const { sale, action } = confirm;
    setBusy(true);
    try {
      if (action === "match") {
        await api.post(`/reconciliation/${sale.id}/match`, { order_id: sale.reconciled_to_order_id || "" });
        toast.success("Vendite unite: contate come una sola vendita");
      } else {
        await api.post(`/reconciliation/${sale.id}/reject`);
        toast.success("Segnate come vendite diverse");
      }
      setConfirm(null);
      load(status);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="reconciliation-panel">
      <p className="text-sm text-muted-foreground mb-4">
        Confronta le vendite importate dalla cassa (EasyEvolver) con gli ordini Dogana. La decisione se due vendite coincidono è calcolata dal sistema; qui confermi o rifiuti manualmente.
      </p>

      <Tabs value={status} onValueChange={setStatus}>
        <TabsList className="rounded-full mb-4 flex-wrap h-auto" data-testid="reconciliation-tabs">
          {TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} className="rounded-full" data-testid={`recon-tab-${t.key}`}>
              <t.icon className="w-3.5 h-3.5 mr-1.5" /> {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : rows.length === 0 ? (
        <Card className="p-10 rounded-xl text-center text-muted-foreground" data-testid="recon-empty">
          Nessuna vendita in questo stato.
        </Card>
      ) : (
        <div className="space-y-3" data-testid="recon-list">
          {rows.map((r) => {
            const badge = STATUS_BADGE[r.reconciliation_status] || STATUS_BADGE.none;
            const showActions = canReconcile && (status === "pending" || status === "conflict");
            return (
              <Card key={r.id} className="p-4 rounded-xl" data-testid={`recon-row-${r.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Badge className={`rounded-full ${badge.cls}`} data-testid={`recon-status-${r.id}`}>{badge.label}</Badge>
                    <span className="text-xs text-muted-foreground">{r.reconciliation_method || "—"}</span>
                  </div>
                  {r.order_code && <Badge variant="outline" className="rounded-full text-xs">Ordine {r.order_code}</Badge>}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  <Field label="Data / ora" value={fmtDate(r.created_at || r.date)} testid={`recon-date-${r.id}`} />
                  <Field label="Origine" value={r.source} testid={`recon-source-${r.id}`} />
                  <Field label="Prodotto" value={r.product} testid={`recon-product-${r.id}`} />
                  <Field label="Quantità" value={r.qty} testid={`recon-qty-${r.id}`} />
                  <Field label="Importo" value={eur(r.amount)} testid={`recon-amount-${r.id}`} />
                  <Field label="ID POS esterno" value={r.external_pos_id} testid={`recon-extid-${r.id}`} />
                  <Field label="Ordine Dogana" value={r.order_code || r.reconciled_to_order_id} testid={`recon-orderid-${r.id}`} />
                  <Field label="Stato" value={badge.label} testid={`recon-statusfield-${r.id}`} />
                </div>

                {r.conflict_reason && (
                  <div className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 text-red-700 p-2.5 text-sm" data-testid={`recon-reason-${r.id}`}>
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{r.conflict_reason}</span>
                  </div>
                )}

                {showActions ? (
                  <div className="mt-3 pt-3 border-t border-border flex flex-wrap gap-2">
                    <Button size="sm" className="rounded-full" disabled={busy}
                      onClick={() => setConfirm({ sale: r, action: "match" })} data-testid={`recon-same-${r.id}`}>
                      <Link2 className="w-4 h-4 mr-1.5" /> Sono la stessa vendita
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full" disabled={busy}
                      onClick={() => setConfirm({ sale: r, action: "reject" })} data-testid={`recon-diff-${r.id}`}>
                      <Unlink className="w-4 h-4 mr-1.5" /> Sono due vendite diverse
                    </Button>
                  </div>
                ) : (status === "pending" || status === "conflict") && !canReconcile ? (
                  <div className="mt-3 pt-3 border-t border-border flex items-center gap-2 text-xs text-muted-foreground" data-testid={`recon-noperm-${r.id}`}>
                    <ShieldAlert className="w-3.5 h-3.5" /> Solo admin/manager possono riconciliare manualmente.
                  </div>
                ) : null}
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="w-[95vw] max-w-md" data-testid="recon-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{confirm?.action === "match" ? "Confermi la riconciliazione?" : "Confermi che sono diverse?"}</DialogTitle>
            <DialogDescription className="text-sm pt-2">
              {confirm?.action === "match"
                ? "La vendita della cassa e l'ordine Dogana verranno considerati la STESSA vendita: un solo ricavo, un solo food cost, un solo scarico di magazzino. Il record della cassa resta come audit."
                : "Le due vendite verranno considerate DISTINTE: la vendita della cassa resterà come ricavo separato e potrà essere scaricata dal magazzino."}
            </DialogDescription>
          </DialogHeader>
          {confirm?.sale && (
            <div className="rounded-lg border border-border p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Prodotto</span><span className="font-medium">{confirm.sale.product}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Quantità</span><span>{confirm.sale.qty}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Importo</span><span>{eur(confirm.sale.amount)}</span></div>
              {confirm.sale.order_code && <div className="flex justify-between"><span className="text-muted-foreground">Ordine Dogana</span><span>{confirm.sale.order_code}</span></div>}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setConfirm(null)} disabled={busy} data-testid="recon-confirm-cancel">Annulla</Button>
            <Button className="rounded-full" onClick={doAction} disabled={busy} data-testid="recon-confirm-ok">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Conferma"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
